/**
 * The organization's shared skills for the selected vendor — the Skills pane's
 * second tab (item BG).
 *
 * Requested as `visibility=organization`, the org id, and `revit_version` **when a year is
 * chosen** — api-v2 narrows it server-side, which is worth doing because its handler resolves
 * each distinct publisher with its own Supabase admin round-trip, so fewer rows is fewer of
 * those.
 *
 * The year came and went here. It was sent when the tree selected a year; it stopped being sent
 * when the tree became a vendor, because narrowing to one year then would have hidden every
 * other year from the only list that shows what the organization shares. It is sent again now
 * that the filter is one explicit control on the pane's title row (owner): "All versions" asks
 * for everything, exactly as before, and a chosen year asks the server for that year.
 *
 * There is still no vendor filter — api-v2 has no vendor column — so the vendor is applied on
 * arrival (`cloudSkillsForVendor`).
 */
import { useCallback, useEffect, useState } from "react";
import type { CloudSkill, SkillsClient, VendorPick } from "../lib/skills";

export type CloudSkillsState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; skills: CloudSkill[] };

export function useCloudSkills(
  client: SkillsClient | null,
  enabled: boolean,
  pick: VendorPick | null,
  /** The pane's year filter; empty means every year, and sends no `revit_version`. */
  version: string = "",
): { state: CloudSkillsState; refreshing: boolean; reload: () => void } {
  const [state, setState] = useState<CloudSkillsState>({ kind: "idle" });
  const [attempt, setAttempt] = useState(0);
  /**
   * A fetch is in flight over rows that are already on screen.
   *
   * Separate from `state`, which stays `loaded` through a refetch so the list does not blink. That
   * keeping-the-rows is exactly what left a year change with no feedback, so the list has to be
   * told a request is running as well as what to draw.
   */
  const [refreshing, setRefreshing] = useState(false);
  // The vendor alone, so the effect refires when the vendor changes and not when an
  // unrelated part of the pick object is rebuilt.
  const kind = pick?.kind ?? null;

  useEffect(() => {
    if (!client || !enabled || kind === null) return;
    let cancelled = false;
    // Keep the rows on screen while refetching: a year change is now a request, and replacing the
    // list with a spinner on every change of the filter is a flash, not feedback. Only the first
    // fetch for a vendor has nothing to show. (`useSkills` does the same.)
    setState((prev) => (prev.kind === "loaded" ? prev : { kind: "loading" }));
    setRefreshing(true);
    client
      // Narrowed to the chosen year server-side, and to this vendor on arrival.
      .sharedSkills(version ? { version } : {})
      .then((skills) => {
        if (!cancelled) setState({ kind: "loaded", skills });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ kind: "error", message: error instanceof Error ? error.message : String(error) });
      })
      .finally(() => {
        if (!cancelled) setRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client, enabled, attempt, kind, version]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, refreshing, reload };
}
